// Normalizer between G1's internal gameKey variants and Hydra's canonical
// analytics gameKey. Any panel wired to /api/analytics/* that takes a gameKey
// param (trades, candles, cross-market-spread, ticks) must pass through here.
//
// Hydra canonical:  "YYYY-MM-DD|TEAM_A|TEAM_B"  (teams alpha-sorted, no sport)
// G1 variants seen in code:
//   - canonical pass-through from Hydra (marketStore exposes `canonicalGameKey`)
//   - "${sport}|${date}|${awayCode}|${homeCode}"  (marketStore fallback when no canonical)
//   - "${date}|${away}|${home}"                   (useExposureFilter, order preserved)
//
// Hydra strips `sport` and alpha-sorts the teams so cross-exchange alias
// mismatches (e.g. Kalshi CHW vs Polymarket CWS) collapse into one key.
// See hydra/BEHAVIORS.md "Cross-exchange game matching".

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export interface HydraGameKeyParts {
  date: string;
  teamA: string;
  teamB: string;
}

/**
 * Convert any G1 gameKey variant to Hydra canonical. Returns null if the
 * input doesn't match a known shape so callers can drop malformed keys
 * rather than send garbage upstream.
 */
export function toHydraGameKey(key: string | null | undefined): string | null {
  if (!key) return null;
  const parts = key.split('|');

  let date: string;
  let a: string;
  let b: string;

  if (parts.length === 3) {
    // date|away|home
    [date, a, b] = parts as [string, string, string];
  } else if (parts.length === 4) {
    // sport|date|away|home — drop sport
    [, date, a, b] = parts as [string, string, string, string];
  } else {
    return null;
  }

  if (!DATE_RE.test(date)) return null;
  if (!a || !b) return null;

  const [teamA, teamB] = [a, b].sort();
  return `${date}|${teamA}|${teamB}`;
}

/** Parse a Hydra canonical key into its components. Returns null on any malformed input. */
export function parseHydraGameKey(key: string | null | undefined): HydraGameKeyParts | null {
  if (!key) return null;
  const parts = key.split('|');
  if (parts.length !== 3) return null;
  const [date, teamA, teamB] = parts as [string, string, string];
  if (!DATE_RE.test(date)) return null;
  if (!teamA || !teamB) return null;
  return { date, teamA, teamB };
}

/**
 * True when a G1 gameKey (any variant) refers to the same game as a Hydra
 * canonical key. Use when matching analytics rows back to local store entries.
 */
export function gameKeysMatch(g1Key: string | null | undefined, hydraKey: string | null | undefined): boolean {
  const normalized = toHydraGameKey(g1Key);
  if (!normalized || !hydraKey) return false;
  return normalized === hydraKey;
}
